import React, { useEffect, useRef, memo, useCallback, useMemo } from "react";
import "./Layout.css";
import { useLocation, useNavigate } from "react-router-dom";
import OptimizedTransitionModal from "../TransitionModal/OptimizedTransitionModal";
import { selectStudent } from "../../reduxSlices/studentSlice";
import { useSelector } from "react-redux";
import { EstadoAlumnoCarrera } from "../../enums/alumnoCarrera.enum";
import ModeHeader from "../ModeHeader/ModeHeader";
import DesktopHeader from "../DesktopHeader/DesktopHeader";
import BlockedScreen from "../BlockedScreen/BlockedScreen";
import { selectInTransitInRquest, selectPanelFilters, setMobileFeatures, selectFlags, setHasRedirectedToCarrera, selectTurnstileVerified, setTurnstileVerified, selectOfertaInvalidandoOVenciendo, selectIsLoggingOut } from "../../reduxSlices/globalFlagsSlice";
import ModalOverlay from "../ModalOverlay/ModalOverlay";
import PanelDrawer from "../PanelDrawer/PanelDrawer";
import { selectDetailSubjectOffered } from "../../reduxSlices/subjectsSlice";
import PanelFilters from "../PanelFilters/PanelFilters";
import { selectParameter, setServerError } from "../../reduxSlices/parametersSlice";
import BannerPrioridad from "../BannerPrioridad/BannerPrioridad";
import MobileNav from "../MobileNav/MobileNav";
import MobileMenu from "../MobileMenu/MobileMenu";
import { useAppDispatch } from "../../app/store";
import BannerConsultaPrioridad from "../BannerConsultaPrioridad/BannerConsultaPrioridad";
import BannersContainer from "../BannersContainer/BannersContainer";
import Turnstile from "react-turnstile";
import { useVerifyTurnstile } from "../../hooks/useTurnstile";
import { useToastError } from "../../hooks/useToast";

interface IPropsLayout {
  children: React.ReactNode;
  sessionExpired: boolean;
}

const MobileLayout = ({ children, sessionExpired }: IPropsLayout) => {
  const dispatch = useAppDispatch();
  const student = useSelector(selectStudent);
  const { handleVerify } = useVerifyTurnstile();
  const { serverError, appState } = useSelector(selectParameter);
  const detailSubjectOffered = useSelector(selectDetailSubjectOffered);
  const panelFilters = useSelector(selectPanelFilters);
  const { hasRedirectedToCarrera } = useSelector(selectFlags);
  const turnstileVerified = useSelector(selectTurnstileVerified);
  const isLoggingOut = useSelector(selectIsLoggingOut);
  const location = useLocation();
  const navigate = useNavigate();
  const micrsoftSession = sessionStorage.getItem("msal.account.keys");
  const ofertaInvalidaOVencida = useSelector(selectOfertaInvalidandoOVenciendo)
  const hasRedirected = useRef(false);
  const showError = useToastError();

  useEffect(() => {
    // set ON mobile features
    dispatch(setMobileFeatures(true));
    const token = localStorage.getItem("token");
    const campusToken = localStorage.getItem("campusToken");
    if (student.bloqueoActivo && !isLoggingOut) {
      navigate("/bloqueo");
    }
    if (serverError) {
      const errorMessage = "<p style='white-space: nowrap'>Por favor intentá de nuevo en unos minutos.</p><p>De persistir el problema avisanos por acá: <a href='https://utdt.edu/soporte' target='_blank' rel='noopener noreferrer' style='color: #2277da; text-decoration: none;'>utdt.edu/soporte</a></p>";
      showError(errorMessage, undefined, "Surgió un error", false);
      dispatch(setServerError(false));
    }
    if (sessionExpired) {
      localStorage.clear()
      sessionStorage.clear();
      console.log("MOBILE SESSION EXPIRED>> REDIRIGIENDO A LOGIN", { token, campusToken });
      navigate("/login");
    }
    if (location.pathname === "/" && !micrsoftSession && (appState === "0" || appState === "4") && localStorage.getItem("token") === null) {
      console.log("MOBILE LOGIN>> REDIRIGIENDO A LOGIN", { token, campusToken });
      localStorage.clear()
      navigate("/login");
    }
    if (!hasRedirectedToCarrera && student.listadoCarreras?.length > 1 && student.studentInitState === 2) {
      dispatch(setHasRedirectedToCarrera(true));
      navigate('/carrera');
    }
  }, [serverError, sessionExpired, student.bloqueoActivo, student.listadoCarreras?.length, student.studentInitState, hasRedirectedToCarrera, isLoggingOut]);

  const excludeFromTurnstile = useMemo(
    () => location.pathname.includes("login") || location.pathname === "/carrera",
    [location.pathname]
  );

  const shouldShowNavigation = useMemo(
    () => !location.pathname.includes("login") && location.pathname !== "/bloqueo",
    [location.pathname]
  );

  const renderContent = useCallback(() => {
    if (excludeFromTurnstile) {
      return <div className="mobile-layout-content">
        {children}
      </div>
    }

    if (turnstileVerified) {
      return <div className="mobile-layout-content">
        {shouldShowNavigation && (
          <>
            <MobileNav />
            {ofertaInvalidaOVencida ? null : <><BannersContainer />
              <MobileMenu /></>}
          </>
        )}
        {children}
      </div>
    }
    return <div className="flex justify-center items-center h-full">
      <Turnstile sitekey={process.env.REACT_APP_TURNSTILE_SITEKEY!} onVerify={handleVerify} />
    </div>
  }, [excludeFromTurnstile, turnstileVerified, shouldShowNavigation, ofertaInvalidaOVencida, children, handleVerify])

  return (
    <div className="mobile-layout">
      <ModeHeader />
      {/* ------------------------------- HEADER Menu ------------------------------ */}
      {!location.pathname.includes("login") &&
        location.pathname !== "/bloqueo" &&
        location.pathname !== "/usuario" &&
        location.pathname !== "/error" && <DesktopHeader />}
      {/* -------------------------------- CHILDREN -------------------------------- */}
      {renderContent()}
      {/* -------------------------------------------------------------------------- */
      /*                                   MODALES                                  */
      /* -------------------------------------------------------------------------- */}
      {/* ------------------------------ MODAL General ----------------------------- */}
      {student.id === 0 &&
        !student.bloqueoActivo &&
        (location.pathname === "/" || location.pathname === "/mis-cursos" || location.pathname === "/calendario") && (
          <OptimizedTransitionModal open={true} text={"logoAnimado"} />
        )}
      {/* --------------------------- MODAL Inicializando -------------------------- */}
      {student.studentInitState === EstadoAlumnoCarrera.INICIALIZACION_KV_PENDIENTE && (
        <OptimizedTransitionModal
          open={true}
          text={"Inicializando la información del alumno. Esta operación puede demorar unos minutos."}
        />
      )}
      {/* --------------------------- Overlay espera -------------------------- */}
      {/* {inTransitInscriptionRequet && <ModalOverlay text={""} />} */}
      {/* ----------------------- Panel Drawe Subject Detail ----------------------- */}
      {location.pathname !== "/login" && detailSubjectOffered && <PanelDrawer subject={detailSubjectOffered} />}
      {location.pathname !== "/login" && panelFilters && <PanelFilters />}
    </div>
  );
};

export default MobileLayout;